import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import {
  User,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  signInWithPopup,
  signInWithCustomToken,
  GoogleAuthProvider,
  AuthError,
} from "firebase/auth";
import { auth } from "./firebase";
import * as loginCode from "./loginCode";

const googleProvider = new GoogleAuthProvider();

interface AuthContextType {
  user: User | null;
  loading: boolean;
  requestLoginCode: (email: string) => Promise<void>;
  verifyLoginCode: (email: string, code: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const requestLoginCode = (email: string) => loginCode.requestLoginCode(email);

  const verifyLoginCode = async (email: string, code: string) => {
    const token = await loginCode.verifyLoginCode(email, code);

    // Sign in with the custom token
    const userCredential = await signInWithCustomToken(auth, token);
    // Force token refresh to get custom claims
    await userCredential.user.getIdToken(true);
  };

  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      // Force token refresh to get updated custom claims from blocking function
      await result.user.getIdToken(true);
    } catch (error) {
      const authError = error as AuthError;
      console.error("Google Sign-In Error:", authError.code, authError.message);

      if (authError.code === "auth/popup-closed-by-user") {
        throw new Error("Sign-in was cancelled.");
      } else if (authError.code === "auth/unauthorized-domain") {
        throw new Error("This domain is not authorized for sign-in.");
      } else if (authError.code === "auth/internal-error") {
        throw new Error("Sign-in failed. Please try again later.");
      }
      throw error;
    }
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  const value = {
    user,
    loading,
    requestLoginCode,
    verifyLoginCode,
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
